import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";
import { Mail, Paperclip, AlertTriangle, CheckCircle2, Loader2, Download } from "lucide-react";

const KIND_META = {
  order_confirmation: { label: "Confirmación", tone: "bg-navy text-cream" },
  ups_label:          { label: "Etiqueta UPS", tone: "bg-royal text-white" },
  fraud_alert:        { label: "Alerta fraude", tone: "bg-royal text-white" },
};

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "order_confirmation", label: "Confirmaciones" },
  { key: "ups_label", label: "Etiquetas UPS" },
  { key: "fraud_alert", label: "Alertas de fraude" },
];

export default function AdminEmails() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [emails, setEmails] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/admin/emails", { params: filter ? { kind: filter } : {} })
      .then(({ data }) => setEmails(data))
      .catch(() => toast.error("Error cargando emails"));
  }, [filter]);

  const openEmail = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/emails/${id}`);
      setDetail(data);
    } catch { toast.error("No se pudo abrir el email"); }
  };

  const downloadAttachment = async (id, idx, filename) => {
    try {
      const token = localStorage.getItem("pf_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/emails/${id}/attachment/${idx}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch { toast.error("No se pudo descargar el adjunto"); }
  };

  if (!emails) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 strokeWidth={1.25} className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-emails-page">
      <div className="mb-8">
        <div className="font-mono-caps text-xs text-muted-foreground">
          <Link to="/admin" className="hover:text-navy">Admin</Link> · Emails
        </div>
        <h1 className="font-serif text-4xl md:text-5xl mt-3 flex items-center gap-3">
          <Mail strokeWidth={1.25} className="h-9 w-9" /> Buzón de emails
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Correos generados por el sistema (mock provider). Cuando conectes Resend / SendGrid se enviarán realmente.
        </p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="email-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`font-mono-caps text-xs px-4 py-2 border transition-colors ${filter === f.key ? "bg-navy text-cream border-navy" : "border-border hover:border-navy"}`}
            data-testid={`email-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-2" data-testid="email-list">
          {emails.length === 0 && (
            <div className="border border-border p-12 text-center text-muted-foreground text-sm">Sin emails para este filtro.</div>
          )}
          {emails.map((e) => {
            const meta = KIND_META[e.tags?.kind] || { label: "Otro", tone: "bg-muted text-muted-foreground" };
            const isActive = selected === e.email_id;
            return (
              <button
                key={e.email_id}
                onClick={() => openEmail(e.email_id)}
                className={`w-full text-left border p-4 transition-colors ${isActive ? "border-navy bg-navy/5" : "border-border hover:border-navy"}`}
                data-testid={`email-${e.email_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className={`inline-block font-mono-caps text-[10px] px-2 py-1 ${meta.tone}`}>{meta.label}</span>
                  <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("es-ES")}</span>
                </div>
                <div className="font-serif text-base leading-snug mb-1 truncate">{e.subject}</div>
                <div className="text-xs text-muted-foreground truncate">
                  <span className="font-mono-caps">De:</span> {e.from} · <span className="font-mono-caps">Para:</span> {e.to}
                </div>
                {e.attachments?.length > 0 && (
                  <div className="text-xs text-royal mt-2 inline-flex items-center gap-1"><Paperclip strokeWidth={1.25} className="h-3 w-3" /> {e.attachments.length} adjunto(s)</div>
                )}
              </button>
            );
          })}
        </div>

        <div className="border border-border bg-white p-6 h-fit sticky top-24" data-testid="email-detail">
          {!detail && <div className="text-center text-muted-foreground py-16 text-sm">Selecciona un email para ver detalles.</div>}
          {detail && (
            <>
              <div className="pb-4 border-b mb-4">
                <div className="font-serif text-xl">{detail.subject}</div>
                <div className="text-xs text-muted-foreground mt-2">
                  <div><strong>De:</strong> {detail.from}</div>
                  <div><strong>Para:</strong> {detail.to}</div>
                  <div><strong>Estado:</strong> {detail.status === "sent" ? <span className="text-navy inline-flex items-center gap-1"><CheckCircle2 strokeWidth={1.25} className="h-3 w-3" /> Enviado</span> : <span className="text-royal inline-flex items-center gap-1"><AlertTriangle strokeWidth={1.25} className="h-3 w-3" /> {detail.status}</span>}</div>
                </div>
              </div>
              {detail.attachments?.length > 0 && (
                <div className="mb-4">
                  <div className="font-mono-caps text-xs mb-2">Adjuntos</div>
                  {detail.attachments.map((a, i) => (
                    <button
                      key={i}
                      onClick={() => downloadAttachment(detail.email_id, i, a.filename)}
                      className="inline-flex items-center gap-2 text-xs border border-navy px-3 py-2 mr-2 hover:bg-navy hover:text-cream transition-colors"
                      data-testid={`email-attachment-${i}`}
                    >
                      <Download strokeWidth={1.25} className="h-3 w-3" /> {a.filename}
                    </button>
                  ))}
                </div>
              )}
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail.body_html }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
